import { useState } from "react";
import { Empty, Space, Typography } from "antd";
import { FileImageOutlined } from "@ant-design/icons";
import VehicleSelect from "./VehicleSelect";
import WorkflowUploadCard from "./WorkflowUploadCard";
import ExtractedDataCard from "./ExtractedDataCard";
import { uploadImageToWorkflow } from "../../services/workflowUpload";
import { IExtractedData } from "../types/workflow.types";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { toastError, toastSuccess } from "../../utils/toast";

const { Text } = Typography;

const SLOTS = [
  { label: "Challan Slip", docType: "challan_slip" },
  { label: "Truck Sheet", docType: "truck_sheet" },
  { label: "Form 515", docType: "form_515" },
  { label: "Gate Pass", docType: "vehicle_gatepass" },
  { label: "Loading Point Weighment Slip", docType: "weightment_slip" },
] as const;

interface SlotState {
  file: File | null;
  uploading: boolean;
  uploaded: boolean;
  extractedData: IExtractedData | null;
}

const emptySlot = (): SlotState => ({
  file: null,
  uploading: false,
  uploaded: false,
  extractedData: null,
});

const SupervisorVerificationTab = () => {
  const [vehicleNumber, setVehicleNumber] = useState<string | null>(null);
  const [slots, setSlots] = useState<SlotState[]>(() => SLOTS.map(emptySlot));

  const handleVehicleChange = (next: string | null) => {
    setVehicleNumber(next);
    setSlots(SLOTS.map(emptySlot));
  };

  const updateSlot = (index: number, patch: Partial<SlotState>) => {
    setSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, ...patch } : slot))
    );
  };

  const handleUpload = async (index: number) => {
    const slot = slots[index];
    if (!slot.file || !vehicleNumber) return;
    updateSlot(index, { uploading: true });
    try {
      const result = await uploadImageToWorkflow(
        slot.file,
        SLOTS[index].docType,
        vehicleNumber
      );
      updateSlot(index, {
        uploading: false,
        uploaded: true,
        extractedData: result.extractedData,
      });
      toastSuccess(`${SLOTS[index].label} uploaded.`);
    } catch (error) {
      updateSlot(index, { uploading: false });
      toastError(
        getErrorMessage(error, "Could not upload the photo. Please try again.")
      );
    }
  };

  const attachedCount = slots.filter((slot) => slot.file !== null).length;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%", maxWidth: 640 }}>
      <div>
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          Vehicle Number
        </Text>
        <VehicleSelect value={vehicleNumber} onChange={handleVehicleChange} />
      </div>

      {!vehicleNumber ? (
        <Empty description="Select a vehicle to upload its documents" />
      ) : (
        <>
          <Text type="secondary">
            {attachedCount} of {SLOTS.length} documents attached
          </Text>
          {SLOTS.map((slot, index) => (
            <div key={slot.docType}>
              <WorkflowUploadCard
                title={slot.label}
                icon={<FileImageOutlined />}
                file={slots[index].file}
                uploading={slots[index].uploading}
                uploaded={slots[index].uploaded}
                onFileSelected={(selected) =>
                  updateSlot(index, {
                    file: selected,
                    uploaded: false,
                    extractedData: null,
                  })
                }
                onRemove={() => updateSlot(index, emptySlot())}
                onUpload={() => handleUpload(index)}
              />
              {slots[index].extractedData && (
                <div style={{ marginTop: 12 }}>
                  <ExtractedDataCard data={slots[index].extractedData!} />
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </Space>
  );
};

export default SupervisorVerificationTab;
